import type {
  DraftChange,
  DraftView,
  DuplicateMatch,
  LibraryItem,
  LibraryMetadata,
  LibraryTree,
} from '@/lib/api/requirement-library';

/**
 * Pure logic behind the requirement library screen (T-0141): search
 * matching and highlighting, tree counts, and laying a module's draft
 * over its published rows. No React and no fetch, so the behaviour that
 * can break silently is unit tested directly.
 *
 * Behavioural reference: `libCompute` / `libItems` / `draftChanges` in
 * `docs/requirement-library/prototype/selection.js`.
 */

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

/** Lower-cased whitespace-separated search terms; empty for a blank query. */
export function searchTerms(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** What a requirement is searchable by. */
export interface Searchable {
  number: string;
  v1_number: string | null;
  module_name: string;
  subcategory: string;
  text: string;
}

/**
 * True when every term appears somewhere in the requirement's number, old
 * Caliber v1 number, module, sub-category or text. A v1 number is also
 * matched with its punctuation ignored, so "AP06" and "AP-06" both find
 * the requirement that was AP06 in v1.
 */
/** True when a term matches the old Caliber v1 number (punctuation ignored). */
export function matchesV1Number(v1Number: string | null, terms: ReadonlyArray<string>): boolean {
  if (!v1Number) return false;
  const bareV1 = v1Number.toLowerCase().replace(/[^a-z0-9]/g, '');
  return terms.some((t) => {
    const bare = t.replace(/[^a-z0-9]/g, '');
    return bare.length > 0 && bareV1.includes(bare);
  });
}

export function matchesSearch(item: Searchable, terms: ReadonlyArray<string>): boolean {
  if (terms.length === 0) return true;
  const v1 = (item.v1_number ?? '').toLowerCase();
  const bareV1 = v1.replace(/[^a-z0-9]/g, '');
  const haystack = [item.number, v1, item.module_name, item.subcategory, item.text]
    .join('\n')
    .toLowerCase();
  return terms.every((t) => {
    if (haystack.includes(t)) return true;
    const bare = t.replace(/[^a-z0-9]/g, '');
    return bare.length > 0 && bareV1.includes(bare);
  });
}

export interface HighlightSegment {
  text: string;
  match: boolean;
}

/**
 * Split `text` into runs that do and do not match a search term, for
 * rendering with `<mark>`. Overlapping and adjacent matches are merged.
 */
export function highlightSegments(text: string, terms: ReadonlyArray<string>): HighlightSegment[] {
  if (!text) return [];
  const lower = text.toLowerCase();
  const ranges: Array<[number, number]> = [];
  for (const term of terms) {
    if (!term) continue;
    let at = lower.indexOf(term);
    while (at >= 0) {
      ranges.push([at, at + term.length]);
      at = lower.indexOf(term, at + term.length);
    }
  }
  if (ranges.length === 0) return [{ text, match: false }];
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1]);
    else merged.push([r[0], r[1]]);
  }
  const out: HighlightSegment[] = [];
  let cursor = 0;
  for (const [start, end] of merged) {
    if (start > cursor) out.push({ text: text.slice(cursor, start), match: false });
    out.push({ text: text.slice(start, end), match: true });
    cursor = end;
  }
  if (cursor < text.length) out.push({ text: text.slice(cursor), match: false });
  return out;
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** Pending change on a row while its module is being edited. */
export type RowChange = 'added' | 'restored' | 'reworded' | 'retired';

export interface LibraryRow extends Searchable {
  item_id: string;
  module_id: string;
  category_name: string;
  source_kind: string;
  /** Null for a requirement that exists only in the draft. */
  version: number | null;
  /** Pending draft change, when the module is being edited. */
  change: RowChange | null;
  /** Published wording, when the draft rewords it. */
  previous: { subcategory: string; text: string } | null;
  /** Unresolved look-alikes: flagged until kept or discarded. */
  duplicates: ReadonlyArray<DuplicateMatch>;
  /** True once the curator chose "Keep both" for this wording. */
  duplicatesKept: boolean;
  /**
   * The citation of a requirement that exists only in the draft (the
   * published payload carries the kind alone; the panel loads the rest).
   */
  source_ref?: unknown;
  /** Labelled fields kept beside the wording (Tag, Live, Basis, Proof, Pending). */
  metadata?: LibraryMetadata | null;
}

/**
 * Why two requirements were flagged as alike, in plain words (T-0150):
 * `exact` the same wording, `wording` most of the same words, `meaning`
 * different words that mean the same. Null when the server did not say.
 */
export function matchKindWords(kind: string | null | undefined): string | null {
  switch (kind) {
    case 'exact':
      return 'same wording';
    case 'wording':
      return 'similar wording';
    case 'meaning':
      return 'similar meaning';
    default:
      return null;
  }
}

/**
 * The flag on a look-alike: "Same wording as AP-008", "Similar meaning to
 * AP-008". Falls back to "Same as" / "Similar to" when the kind is unknown.
 */
export function duplicateFlagLabel(d: {
  number: string;
  exact: boolean;
  match_kind?: string | undefined;
}): string {
  const words = matchKindWords(d.match_kind);
  if (!words) return `${d.exact ? 'Same as' : 'Similar to'} ${d.number}`;
  const label = words.charAt(0).toUpperCase() + words.slice(1);
  return `${label} ${d.match_kind === 'exact' ? 'as' : 'to'} ${d.number}`;
}

export function rowFromItem(item: LibraryItem): LibraryRow {
  return {
    item_id: item.item_id,
    number: item.number,
    v1_number: item.v1_number,
    module_id: item.module_id,
    module_name: item.module_name,
    category_name: item.category_name,
    subcategory: item.subcategory,
    text: item.text,
    source_kind: item.source_kind,
    metadata: item.metadata ?? null,
    version: item.version,
    change: null,
    previous: null,
    duplicates: [],
    duplicatesKept: false,
  };
}

function asRowChange(change: DraftChange): RowChange {
  if (change.change === 'retired' || change.op === 'retire') return 'retired';
  if (change.change === 'reworded' || change.op === 'edit') return 'reworded';
  return change.change === 'restored' ? 'restored' : 'added';
}

export interface DraftModuleInfo {
  module_id: string;
  module_name: string;
  category_name: string;
}

/**
 * A module's rows with its draft laid over them: reworded rows carry the
 * draft wording, retired rows stay in place (struck through by the grid),
 * and added rows are appended. Rows of a draft-only module come entirely
 * from the draft.
 */
export function applyDraftToRows(
  published: ReadonlyArray<LibraryRow>,
  draft: DraftView | null | undefined,
  module: DraftModuleInfo,
): LibraryRow[] {
  if (!draft) return published.slice();
  const byItem = new Map(draft.changes.map((c) => [c.item_id, c]));
  const overlay = (row: LibraryRow, c: DraftChange): LibraryRow => {
    const change = asRowChange(c);
    const kept = c.duplicate_decision != null;
    return {
      ...row,
      subcategory: change === 'retired' ? row.subcategory : (c.subcategory ?? row.subcategory),
      text: change === 'retired' ? row.text : (c.text ?? row.text),
      metadata: change === 'retired' || c.metadata === undefined ? row.metadata : c.metadata,
      change,
      previous: c.previous,
      duplicates: change === 'retired' || kept ? [] : c.duplicates,
      duplicatesKept: kept && c.duplicates.length > 0,
    };
  };
  const out: LibraryRow[] = [];
  const seen = new Set<string>();
  for (const row of published) {
    const c = byItem.get(row.item_id);
    seen.add(row.item_id);
    out.push(c ? overlay(row, c) : row);
  }
  for (const c of draft.changes) {
    if (seen.has(c.item_id) || c.op === 'retire') continue;
    out.push(
      overlay(
        {
          item_id: c.item_id,
          number: c.number,
          v1_number: null,
          module_id: module.module_id,
          module_name: module.module_name,
          category_name: module.category_name,
          subcategory: c.subcategory ?? '',
          text: c.text ?? '',
          source_kind: c.source_kind ?? 'curator',
          source_ref: c.source_ref,
          version: null,
          change: 'added',
          previous: null,
          duplicates: [],
          duplicatesKept: false,
        },
        c,
      ),
    );
  }
  return out;
}

/** Group key for the grid: one group per module and sub-category. */
export function groupKeyOf(row: Pick<LibraryRow, 'module_id' | 'subcategory'>): string {
  return `${row.module_id}\u0000${row.subcategory}`;
}

/**
 * Make each (module, sub-category) group contiguous, keeping groups in
 * order of first appearance and rows in their original order within a
 * group. The grid needs this: a published version appends new
 * requirements at the end of the module, not at the end of their
 * sub-category.
 */
export function groupRows(rows: ReadonlyArray<LibraryRow>): LibraryRow[] {
  const groups = new Map<string, LibraryRow[]>();
  for (const row of rows) {
    const key = groupKeyOf(row);
    const bucket = groups.get(key);
    if (bucket) bucket.push(row);
    else groups.set(key, [row]);
  }
  return Array.from(groups.values()).flat();
}

// ---------------------------------------------------------------------------
// Tree counts
// ---------------------------------------------------------------------------

export interface TreeCounts {
  /** Count per module id. */
  byModule: Map<string, number>;
  /** Count per category id. */
  byCategory: Map<string, number>;
  total: number;
}

/**
 * Counts for the navigation tree. With no search these are the published
 * counts from the tree; with a search they are the number of matching
 * requirements in each module.
 */
export function treeCounts(
  tree: LibraryTree,
  items: ReadonlyArray<Searchable & { module_id: string }>,
  terms: ReadonlyArray<string>,
): TreeCounts {
  const byModule = new Map<string, number>();
  if (terms.length === 0) {
    for (const c of tree.categories) for (const m of c.modules) byModule.set(m.id, m.item_count);
  } else {
    for (const c of tree.categories) for (const m of c.modules) byModule.set(m.id, 0);
    for (const item of items) {
      if (matchesSearch(item, terms)) {
        byModule.set(item.module_id, (byModule.get(item.module_id) ?? 0) + 1);
      }
    }
  }
  const byCategory = new Map<string, number>();
  let total = 0;
  for (const c of tree.categories) {
    const n = c.modules.reduce((sum, m) => sum + (byModule.get(m.id) ?? 0), 0);
    byCategory.set(c.id, n);
    total += n;
  }
  return { byModule, byCategory, total };
}

/**
 * The tree as engagements see it: active categories and active modules
 * only. Retired ones are listed for curators on request, but they are not
 * part of what the library offers and are left out of its counts.
 */
export function activeTree(tree: LibraryTree): LibraryTree {
  return {
    ...tree,
    categories: tree.categories
      .filter((c) => c.status === 'active')
      .map((c) => ({ ...c, modules: c.modules.filter((m) => m.status === 'active') })),
  };
}

// ---------------------------------------------------------------------------
// Draft summaries
// ---------------------------------------------------------------------------

/** The name a draft renames its module to, or null when it does not. */
export function pendingRename(draft: DraftView | null | undefined): string | null {
  return draft?.pending_name ?? null;
}

/**
 * Number of unpublished changes in a draft: one per changed requirement,
 * plus one when the module is being renamed.
 */
export function draftChangeCount(draft: DraftView | null | undefined): number {
  return draft ? draft.changes.length + (pendingRename(draft) != null ? 1 : 0) : 0;
}

/** Changes whose look-alike flag has not been kept or discarded. */
export function unresolvedDuplicateCount(draft: DraftView | null | undefined): number {
  if (!draft) return 0;
  return draft.changes.filter(
    (c) => c.op !== 'retire' && c.duplicates.length > 0 && c.duplicate_decision == null,
  ).length;
}

export interface PublishChange {
  item_id: string;
  kind: 'Renamed' | 'Added' | 'Restored' | 'Reworded' | 'Retired';
  /** Empty for the rename, which is a change to the module, not to a requirement. */
  number: string;
  text: string;
}

/** List key of the module rename in the publish dialog. */
export const RENAME_CHANGE_ID = '__rename__';

/** The change list shown in the publish dialog: the rename first, then the requirements. */
export function publishChanges(draft: DraftView | null | undefined): PublishChange[] {
  if (!draft) return [];
  const renamedTo = pendingRename(draft);
  const rename: PublishChange[] =
    renamedTo != null
      ? [
          {
            item_id: RENAME_CHANGE_ID,
            kind: 'Renamed',
            number: '',
            text: `${draft.module_name} becomes ${renamedTo}`,
          },
        ]
      : [];
  return [...rename, ...requirementChanges(draft)];
}

function requirementChanges(draft: DraftView): PublishChange[] {
  const label: Record<RowChange, Exclude<PublishChange['kind'], 'Renamed'>> = {
    added: 'Added',
    restored: 'Restored',
    reworded: 'Reworded',
    retired: 'Retired',
  };
  return draft.changes.map((c) => ({
    item_id: c.item_id,
    kind: label[asRowChange(c)],
    number: c.number,
    text: c.text ?? c.previous?.text ?? '',
  }));
}

/** Why a draft cannot be published yet, or null when it can. */
export function publishBlocker(draft: DraftView | null | undefined): string | null {
  if (!draft || draftChangeCount(draft) === 0) {
    return 'There are no changes to publish. Add, reword or retire a requirement, or rename the module, first.';
  }
  if (draft.resulting_item_count === 0) {
    return 'A module with no active requirements cannot be published. Keep or add at least one requirement.';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Module names
// ---------------------------------------------------------------------------

/**
 * The same normalisation the server applies to module names
 * (`normaliseKey` in `@caliber-2/requirement-library`): case, spacing,
 * punctuation and "&" versus "and" do not make a name different. Used
 * only to warn before saving; the server check is the one that counts.
 */
export function moduleNameKey(name: string): string {
  return name
    .normalize('NFKC')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

/**
 * The module in the tree whose name is the same as `name`, if any.
 * `exceptModuleId` leaves one module out (the one being renamed).
 */
export function findModuleByName(
  tree: LibraryTree | undefined,
  name: string,
  exceptModuleId?: string | null,
): { id: string; name: string; status: string } | null {
  const key = moduleNameKey(name);
  if (!tree || !key) return null;
  for (const c of tree.categories) {
    for (const m of c.modules) {
      if (m.id === exceptModuleId) continue;
      if (moduleNameKey(m.name) === key) return { id: m.id, name: m.name, status: m.status };
    }
  }
  return null;
}

/** The category in the tree whose name is the same as `name`, if any. */
export function findCategoryByName(
  tree: LibraryTree | undefined,
  name: string,
): { id: string; name: string; status: string } | null {
  const key = moduleNameKey(name);
  if (!tree || !key) return null;
  for (const c of tree.categories) {
    if (moduleNameKey(c.name) === key) return { id: c.id, name: c.name, status: c.status };
  }
  return null;
}

// ---------------------------------------------------------------------------
// URL parameters
// ---------------------------------------------------------------------------

/**
 * A URL search value as the screens use it: an absent, empty or
 * whitespace-only value all mean "not set" (`?module=` is the same as no
 * `module` at all), and anything else is trimmed.
 */
export function blankToNull(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function plural(n: number, noun: string, pluralNoun = `${noun}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? noun : pluralNoun}`;
}

/** "1 Sep 2026", in the reader's time zone. Empty for a missing date. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Built by hand: `toLocaleDateString` spells months differently from one
  // runtime to the next ("Sep" / "Sept").
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** URL state shared by the library and the engagement requirements screens. */
export interface LibrarySearch {
  q?: string;
  module?: string;
  /** T-0157: an AI intake run to open in the intake sheet (the run watcher's Open, the bell). */
  intake_run?: string;
}

/**
 * Read `q` and `module` from the URL. A parameter that is present but
 * empty (`?module=`, `?q=`) or only whitespace is dropped, so it behaves
 * exactly like a parameter that is not there: all modules, no search. A
 * value of another type (the router parses `?module=12` as a number) is
 * read as its text.
 */
export function parseLibrarySearch(search: Record<string, unknown>): LibrarySearch {
  const text = (value: unknown): string | null =>
    typeof value === 'string'
      ? value
      : typeof value === 'number' && Number.isFinite(value)
        ? String(value)
        : null;
  const q = text(search['q']);
  const moduleId = blankToNull(text(search['module']));
  const intakeRun = blankToNull(text(search['intake_run']));
  return {
    ...(q != null && q.trim().length > 0 ? { q } : {}),
    ...(moduleId != null ? { module: moduleId } : {}),
    ...(intakeRun != null ? { intake_run: intakeRun } : {}),
  };
}
